/**
 * Credentials Service
 * CRUD műveletek a credentials kezeléséhez
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Credential, CredentialType } from '../types/credentials';
import CryptoJS from 'crypto-js';

const CREDENTIALS_COLLECTION = 'credentials';
const ENCRYPTION_KEY = process.env.REACT_APP_CREDENTIALS_ENCRYPTION_KEY || 'default-key-change-in-production';

/**
 * Titkosítás AES-256 algoritmussal
 */
function encrypt(text: string): string {
  return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
}

/**
 * Visszafejtés
 */
function decrypt(ciphertext: string): string {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Decryption error:', error);
    return ciphertext;
  }
}

/**
 * Credential adatok titkosítása
 */
function encryptCredentialData(data: Record<string, any>): Record<string, any> {
  const encrypted: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'object' && value !== null) {
      // Nested objektumok rekurzív titkosítása
      encrypted[key] = encryptCredentialData(value);
    } else if (typeof value === 'string' && shouldEncryptField(key)) {
      // Érzékeny mezők titkosítása
      encrypted[key] = encrypt(value);
    } else {
      encrypted[key] = value;
    }
  }
  
  return encrypted;
}

/**
 * Credential adatok visszafejtése
 */
function decryptCredentialData(data: Record<string, any>): Record<string, any> {
  const decrypted: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'object' && value !== null) {
      // Nested objektumok rekurzív visszafejtése
      decrypted[key] = decryptCredentialData(value);
    } else if (typeof value === 'string' && shouldEncryptField(key)) {
      // Érzékeny mezők visszafejtése
      try {
        decrypted[key] = decrypt(value);
      } catch (error) {
        console.error(`Failed to decrypt field: ${key}`, error);
        decrypted[key] = value;
      }
    } else {
      decrypted[key] = value;
    }
  }
  
  return decrypted;
}

/**
 * Mező titkosítandó-e
 */
function shouldEncryptField(fieldName: string): boolean {
  const sensitiveFields = [
    'pass', 'password', 'apiKey', 'api_key', 'token', 'secret', 
    'clientSecret', 'accessToken', 'refreshToken', 'botToken',
    'connectionString', 'privateKey', 'webhookSecret', 'ipcKey'
  ];
  
  return sensitiveFields.some(field => 
    fieldName.toLowerCase().includes(field.toLowerCase())
  );
}

/**
 * Összes credential lekérése egy szervezethez
 */
export async function getCredentials(organizationId: string): Promise<Credential[]> {
  try {
    const q = query(
      collection(db, CREDENTIALS_COLLECTION),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        data: decryptCredentialData(data.data),
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt
      } as Credential;
    });
  } catch (error) {
    console.error('Error fetching credentials:', error);
    throw error;
  }
}

/**
 * Credential lekérése típus szerint
 */
export async function getCredentialsByType(
  organizationId: string, 
  type: CredentialType
): Promise<Credential[]> {
  try {
    const q = query(
      collection(db, CREDENTIALS_COLLECTION),
      where('organizationId', '==', organizationId),
      where('type', '==', type),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        data: decryptCredentialData(data.data),
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt
      } as Credential;
    });
  } catch (error) {
    console.error('Error fetching credentials by type:', error);
    throw error;
  }
}

/**
 * Egy credential lekérése ID alapján
 */
export async function getCredential(credentialId: string): Promise<Credential | null> {
  try {
    const docRef = doc(db, CREDENTIALS_COLLECTION, credentialId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      data: decryptCredentialData(data.data),
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt
    } as Credential;
  } catch (error) {
    console.error('Error fetching credential:', error);
    throw error;
  }
}

/**
 * Új credential létrehozása
 */
export async function createCredential(
  credential: Omit<Credential, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>
): Promise<string> {
  try {
    const now = Timestamp.now();
    
    const credentialData = {
      ...credential,
      data: encryptCredentialData(credential.data),
      createdAt: now,
      updatedAt: now,
      usageCount: 0
    };
    
    const docRef = await addDoc(collection(db, CREDENTIALS_COLLECTION), credentialData);
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating credential:', error);
    throw error;
  }
}

/**
 * Credential frissítése
 */
export async function updateCredential(
  credentialId: string,
  updates: Partial<Omit<Credential, 'id' | 'createdAt' | 'organizationId' | 'createdBy'>>
): Promise<void> {
  try {
    const docRef = doc(db, CREDENTIALS_COLLECTION, credentialId);
    
    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now()
    };
    
    // Ha data frissül, titkosítjuk
    if (updates.data) {
      updateData.data = encryptCredentialData(updates.data);
    }
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating credential:', error);
    throw error;
  }
}

/**
 * Credential törlése (soft delete)
 */
export async function deleteCredential(credentialId: string): Promise<void> {
  try {
    const docRef = doc(db, CREDENTIALS_COLLECTION, credentialId);
    
    await updateDoc(docRef, {
      isActive: false,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error deleting credential:', error);
    throw error;
  }
}

/**
 * Credential használat számláló növelése
 */
export async function incrementCredentialUsage(credentialId: string): Promise<void> {
  try {
    const docRef = doc(db, CREDENTIALS_COLLECTION, credentialId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const currentCount = docSnap.data().usageCount || 0;
      await updateDoc(docRef, {
        usageCount: currentCount + 1
      });
    }
  } catch (error) {
    console.error('Error incrementing credential usage:', error);
    // Ne dobjunk hibát, ez nem kritikus
  }
}

/**
 * Credential tesztelése (kapcsolat ellenőrzése)
 */
export async function testCredential(credential: Credential): Promise<{ success: boolean; message: string }> {
  try {
    // TODO: Implementáld a credential típus alapján
    // Például SMTP esetén próbálj kapcsolódni
    // OpenAI esetén próbálj egy egyszerű API hívást
    
    return {
      success: true,
      message: 'Credential tesztelés még nem implementált'
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'Ismeretlen hiba'
    };
  }
}
